import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ReceiptLong as ReceiptLongIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import {
  extractExpenseDocument,
  getExpenseDocument,
  listExpenseDocuments,
  MAX_EXPENSE_UPLOAD_BYTES,
  uploadExpenseDocument
} from './api';
import ExpenseDraftReview from './ExpenseDraftReview';

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}\u00a0KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}\u00a0MB`;
}

function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** YYYY-MM-DD as a local calendar date (no time zone shift). */
function formatDay(day) {
  const [y, m, d] = String(day || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMoney(amount, currency) {
  if (amount == null) return '';
  try {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency || 'CAD' }).format(amount);
  } catch (_) {
    return Number(amount).toFixed(2);
  }
}

function typeLabel(mimeType) {
  return mimeType === 'application/pdf' ? 'PDF' : 'Photo';
}

function draftSummary(draft) {
  if (!draft) return null;
  const { id, vendorRawName, transactionDate, total, currency, reviewStatus, duplicateStatus, propertyId, categoryId } =
    draft;
  return { id, vendorRawName, transactionDate, total, currency, reviewStatus, duplicateStatus, propertyId, categoryId };
}

function statusChip(doc) {
  if (!doc.draft) return { label: 'Not read yet', color: 'default' };
  if (doc.draft.reviewStatus === 'needs_review' && doc.status === 'failed') {
    return { label: 'Needs details', color: 'warning' };
  }
  if (doc.draft.reviewStatus === 'needs_review') return { label: 'Needs review', color: 'info' };
  if (doc.draft.reviewStatus === 'approved') return { label: 'In your books', color: 'success' };
  return { label: 'Not an expense', color: 'default' };
}

function errorMessage(err, fallback) {
  if (err?.response?.status === 404) {
    return 'Receipts & bills is not turned on for this account yet.';
  }
  return err?.response?.data?.error || fallback;
}

/**
 * ZRF-EXP-CAP — upload receipts and bills, read them into drafts, review them.
 * Shown inside Financial Center when isExpenseCaptureEnabled().
 */
function ExpenseReceipts({ onBack }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState(null);
  const [review, setReview] = useState(null);
  const cameraInput = useRef(null);
  const libraryInput = useRef(null);
  const fileInput = useRef(null);
  const openReviewId = useRef(null);

  /** Ignores results for a review the landlord already closed. */
  const showReview = (next) => {
    if (openReviewId.current === next.id) setReview(next);
  };

  const closeReview = () => {
    openReviewId.current = null;
    setReview(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listExpenseDocuments();
      setDocuments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your receipts.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (id, docPatch, draft) => {
    setDocuments((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const { previewUrl, previewUrlExpiresAt, draft: _full, ...rest } = docPatch || {};
        return { ...row, ...rest, draft: draft === undefined ? row.draft : draftSummary(draft) };
      })
    );
  };

  const readInto = async (id, doc) => {
    showReview({ id, phase: 'reading', doc });
    const result = await extractExpenseDocument(id);
    updateRow(id, result.document, result.draft);
    showReview({ id, phase: 'ready', doc: { ...doc, ...result.document, draft: result.draft } });
  };

  const openReview = async (id) => {
    openReviewId.current = id;
    setReview({ id, phase: 'loading' });
    try {
      const doc = await getExpenseDocument(id);
      if (doc.draft) {
        showReview({ id, phase: 'ready', doc });
      } else {
        await readInto(id, doc);
      }
    } catch (err) {
      showReview({ id, phase: 'error', error: errorMessage(err, 'Could not open this receipt.') });
    }
  };

  const retryRead = async () => {
    const { id, doc } = review;
    try {
      await readInto(id, doc);
    } catch (err) {
      showReview({ id, phase: 'error', error: errorMessage(err, 'Could not read this receipt.') });
    }
  };

  const handleSaved = (draft, message = 'Saved.') => {
    updateRow(review.id, null, draft);
    closeReview();
    setNotice({ severity: 'success', text: message });
  };

  const handleReopened = (draft, message) => {
    updateRow(review.id, null, draft);
    setReview((prev) => prev && { ...prev, doc: { ...prev.doc, draft }, notice: message });
  };

  const handleFile = async (event, sourceType) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_EXPENSE_UPLOAD_BYTES) {
      setNotice({ severity: 'error', text: 'That file is larger than 15 MB. Try a smaller photo or PDF.' });
      return;
    }
    setNotice(null);
    setProgress(0);
    setUploading(true);
    try {
      const { duplicateOf, ...doc } = await uploadExpenseDocument(file, sourceType, setProgress);
      const uploaded = { ...doc, draft: null };
      setDocuments((prev) => [uploaded, ...prev]);
      if (duplicateOf) {
        setNotice({
          severity: 'warning',
          text: 'This looks like a file you already uploaded, so we didn’t read it again.',
          duplicateOf
        });
      }
      if (!duplicateOf) openReview(uploaded.id);
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Upload failed. Please try again.') });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      {onBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
          Financial Center
        </Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ReceiptLongIcon color="primary" fontSize="large" />
        <Typography variant="h5" component="h1">
          Receipts & bills
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Add a photo or PDF of a receipt or bill. We read the vendor, date and amounts for you to check. Files stay
        private to your account.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PhotoCameraIcon />}
          onClick={() => cameraInput.current?.click()}
          disabled={uploading}
        >
          Take photo
        </Button>
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryIcon />}
          onClick={() => libraryInput.current?.click()}
          disabled={uploading}
        >
          Choose photo
        </Button>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
        >
          Upload file
        </Button>
      </Stack>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid="expense-camera-input"
        onChange={(e) => handleFile(e, 'camera')}
      />
      <input
        ref={libraryInput}
        type="file"
        accept="image/*"
        hidden
        data-testid="expense-library-input"
        onChange={(e) => handleFile(e, 'photo_library')}
      />
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/*"
        hidden
        data-testid="expense-file-input"
        onChange={(e) => handleFile(e, 'file_upload')}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        PDF, JPEG, PNG, HEIC or WebP, up to 15 MB.
      </Typography>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Uploading… {progress}%
          </Typography>
          <LinearProgress variant="determinate" value={progress} aria-label="Upload progress" />
        </Box>
      )}

      {notice && (
        <Alert
          severity={notice.severity}
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
          action={
            notice.duplicateOf ? (
              <Button color="inherit" size="small" onClick={() => openReview(notice.duplicateOf)}>
                Open earlier upload
              </Button>
            ) : undefined
          }
        >
          {notice.text}
        </Alert>
      )}

      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        Your uploads
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading receipts" />
        </Box>
      )}

      {loadError && !loading && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Try again
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {!loading && !loadError && documents.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No receipts yet. Your uploads will appear here.
        </Typography>
      )}

      {!loading && !loadError && documents.length > 0 && (
        <List disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          {documents.map((doc, index) => {
            const chip = statusChip(doc);
            const d = doc.draft;
            const secondary = [
              d?.transactionDate ? formatDay(d.transactionDate) : formatDate(doc.createdAt),
              d?.total != null ? formatMoney(d.total, d.currency) : null,
              typeLabel(doc.mimeType),
              d ? null : formatSize(doc.fileSize)
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ListItem
                key={doc.id}
                divider={index < documents.length - 1}
                secondaryAction={
                  <Button size="small" onClick={() => openReview(doc.id)}>
                    {d && d.reviewStatus !== 'needs_review' ? 'View' : 'Review'}
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <span>{d?.vendorRawName || doc.fileName || `${typeLabel(doc.mimeType)} receipt`}</span>
                      <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
                    </Stack>
                  }
                  secondary={secondary}
                  sx={{ pr: 9 }}
                />
              </ListItem>
            );
          })}
        </List>
      )}

      <ExpenseDraftReview
        review={review}
        onClose={closeReview}
        onRetry={retryRead}
        onSaved={handleSaved}
        onReopened={handleReopened}
      />
    </Box>
  );
}

export default ExpenseReceipts;
